import React from 'react';
import { createRoot } from 'react-dom/client';
import type { WidgetConfig, Message, ConnectionStatus } from './types';
import { ApiClient } from './utils/api';
import { SocketClient } from './utils/socket';
import { ChatBubble } from './components/ChatBubble';
import { ChatWindow } from './components/ChatWindow';
import { LanguageCode } from './components/LanguageSelector';
import './styles/widget.css';

class SecureDevKitChatWidget {
  private config: Required<Omit<WidgetConfig, 'userId' | 'welcomeMessage'>> & {
    userId?: string;
    welcomeMessage?: string;
  };
  private apiClient: ApiClient;
  private socketClient: SocketClient;
  private container: HTMLDivElement | null = null;
  private root: any = null;
  private conversationId: string | null = null;
  private messages: Message[] = [];
  private isOpen = false;
  private isTyping = false;
  private connectionStatus: ConnectionStatus = 'disconnected';
  private unreadCount = 0;
  private selectedLanguage: LanguageCode = 'en';

  constructor(config: WidgetConfig) {
    // Validate required config
    if (!config.appId || !config.apiKey) {
      throw new Error('appId and apiKey are required');
    }

    // Set defaults
    this.config = {
      appId: config.appId,
      apiKey: config.apiKey,
      apiUrl: config.apiUrl || 'http://localhost:3000/api/v1',
      userId: config.userId,
      position: config.position || 'bottom-right',
      primaryColor: config.primaryColor || '#007bff',
      title: config.title || 'Chat Support',
      welcomeMessage: config.welcomeMessage,
      placeholder: config.placeholder || 'Type your message...',
    };

    // Initialize clients
    this.apiClient = new ApiClient(this.config.apiUrl, this.config.apiKey);
    this.socketClient = new SocketClient(
      this.config.apiUrl,
      this.config.apiKey,
      this.config.userId
    );

    // Setup socket event handlers
    this.setupSocketHandlers();

    // Initialize widget
    this.init();
  }

  private setupSocketHandlers() {
    this.socketClient.on('status', (status) => {
      this.connectionStatus = status;
      this.render();
    });

    this.socketClient.on('message', (message) => {
      this.messages.push(message);
      if (!this.isOpen) {
        this.unreadCount++;
      }
      this.render();
    });

    this.socketClient.on('typing:start', () => {
      this.isTyping = true;
      this.render();
    });

    this.socketClient.on('typing:stop', () => {
      this.isTyping = false;
      this.render();
    });
  }

  private async init() {
    // Load saved language preference
    if (typeof window !== 'undefined' && window.localStorage) {
      const savedLanguage = localStorage.getItem(`securedevkit-chat-language-${this.config.appId}`);
      if (savedLanguage && ['en', 'es', 'fr', 'de', 'pt', 'it', 'zh', 'ja', 'ko', 'ar', 'ru'].includes(savedLanguage)) {
        this.selectedLanguage = savedLanguage as LanguageCode;
      }
    }

    // Create container
    this.container = document.createElement('div');
    this.container.id = 'securedevkit-chat-widget';
    document.body.appendChild(this.container);

    // Create React root
    this.root = createRoot(this.container);

    // Fetch widget configuration from API
    try {
      const widgetConfig = await this.apiClient.getWidgetConfig();
      
      // Apply configuration if widget is active
      if (widgetConfig.active) {
        if (widgetConfig.appearance) {
          // Merge appearance config
          this.config = {
            ...this.config,
            position: widgetConfig.appearance.position || this.config.position,
            primaryColor: widgetConfig.appearance.primaryColor || this.config.primaryColor,
            title: widgetConfig.appearance.title || this.config.title,
            placeholder: widgetConfig.appearance.placeholder || this.config.placeholder,
          };
        }
      } else {
        // Widget is disabled, don't render
        console.log('Widget is disabled for this app');
        return;
      }
    } catch (error) {
      console.warn('Failed to fetch widget config, using defaults:', error);
      // Continue with default config
    }

    // Connect socket
    this.socketClient.connect();

    // Load or create conversation
    await this.loadConversation();

    // Initial render
    this.render();
  }

  private async loadConversation() {
    try {
      // Try to get existing conversation from localStorage
      const storedConversationId = localStorage.getItem(
        `securedevkit_conversation_${this.config.appId}`
      );

      if (storedConversationId) {
        try {
          const conversation = await this.apiClient.getConversation(
            this.config.appId,
            storedConversationId
          );
          this.conversationId = conversation.id;
          this.messages = conversation.messages || [];
          this.socketClient.joinConversation(this.conversationId);
          return;
        } catch (error) {
          // Conversation doesn't exist, create new one
          console.log('Stored conversation not found, creating new one');
        }
      }

      // Create new conversation
      const conversation = await this.apiClient.createConversation(
        this.config.appId,
        this.config.userId,
        this.config.title
      );
      this.conversationId = conversation.id;
      localStorage.setItem(
        `securedevkit_conversation_${this.config.appId}`,
        this.conversationId
      );
      this.socketClient.joinConversation(this.conversationId);
    } catch (error: any) {
      console.error('Failed to load conversation:', error);
      this.connectionStatus = 'error';
    }
  }

  private handleSend = async (content: string) => {
    if (!this.conversationId) {
      await this.loadConversation();
    }

    if (!this.conversationId) {
      console.error('No conversation available');
      return;
    }

    try {
      // Optimistically add user message
      const tempUserMessage: Message = {
        id: `temp-${Date.now()}`,
        role: 'user',
        content,
        createdAt: new Date().toISOString(),
      };
      this.messages.push(tempUserMessage);
      this.isTyping = true;
      this.render();

      // Send typing indicator
      this.socketClient.startTyping(this.conversationId!);

      // Send message
      const response = await this.apiClient.sendMessage(
        this.config.appId,
        this.conversationId,
        content
      );

      // Replace temp message with real one
      const userIndex = this.messages.findIndex((m) => m.id === tempUserMessage.id);
      if (userIndex !== -1) {
        this.messages[userIndex] = response.userMessage;
      } else {
        this.messages.push(response.userMessage);
      }

      // Add assistant message
      this.messages.push(response.assistantMessage);

      // Stop typing
      this.socketClient.stopTyping(this.conversationId!);
      this.isTyping = false;
      this.render();
    } catch (error: any) {
      console.error('Failed to send message:', error);
      // Remove temp message on error
      this.messages = this.messages.filter((m) => !m.id.startsWith('temp-'));
      this.isTyping = false;
      this.render();
    }
  };

  private handleToggle = () => {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.unreadCount = 0;
    }
    this.render();
  };

  private handleClose = () => {
    this.isOpen = false;
    this.render();
  };

  private handleLanguageChange = (language: LanguageCode) => {
    this.selectedLanguage = language;
    // Store in localStorage for persistence
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem(`securedevkit-chat-language-${this.config.appId}`, language);
    }
    this.render();
  };

  private render() {
    if (!this.root) return;

    this.root.render(
      <WidgetApp
        isOpen={this.isOpen}
        messages={this.messages}
        isTyping={this.isTyping}
        connectionStatus={this.connectionStatus}
        unreadCount={this.unreadCount}
        config={this.config}
        selectedLanguage={this.selectedLanguage}
        onToggle={this.handleToggle}
        onClose={this.handleClose}
        onSend={this.handleSend}
        onLanguageChange={this.handleLanguageChange}
      />
    );
  }

  destroy() {
    this.socketClient.disconnect();
    if (this.container) {
      document.body.removeChild(this.container);
    }
    if (this.root) {
      this.root.unmount();
    }
  }
}

interface WidgetAppProps {
  isOpen: boolean;
  messages: Message[];
  isTyping: boolean;
  connectionStatus: ConnectionStatus;
  unreadCount: number;
  config: Required<Omit<WidgetConfig, 'userId' | 'welcomeMessage'>> & {
    userId?: string;
    welcomeMessage?: string;
  };
  selectedLanguage: LanguageCode;
  onToggle: () => void;
  onClose: () => void;
  onSend: (message: string) => void;
  onLanguageChange: (language: LanguageCode) => void;
}

const WidgetApp: React.FC<WidgetAppProps> = ({
  isOpen,
  messages,
  isTyping,
  connectionStatus,
  unreadCount,
  config,
  selectedLanguage,
  onToggle,
  onClose,
  onSend,
  onLanguageChange,
}) => {
  return (
    <>
      <ChatBubble
        onClick={onToggle}
        unreadCount={unreadCount}
        position={config.position}
        primaryColor={config.primaryColor}
      />
      {isOpen && (
        <ChatWindow
          title={config.title}
          messages={messages}
          isTyping={isTyping}
          connectionStatus={connectionStatus}
          onSend={onSend}
          onClose={onClose}
          position={config.position}
          placeholder={config.placeholder}
          selectedLanguage={selectedLanguage}
          onLanguageChange={onLanguageChange}
        />
      )}
    </>
  );
};

// Global initialization function
declare global {
  interface Window {
    SecureDevKitChat: {
      init: (config: WidgetConfig) => SecureDevKitChatWidget;
    };
  }
}

window.SecureDevKitChat = {
  init: (config: WidgetConfig) => {
    return new SecureDevKitChatWidget(config);
  },
};

export default SecureDevKitChatWidget;

